import React, { useState } from 'react';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
                     'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function AdminCalendar({ selectedDate, onSelectDate, minDate, disabledDates = [], coloredDates = {} }) {
  // coloredDates = { green: ["2026-03-04", ...], red: ["2026-03-10", ...] }
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selectedDate) {
      const [y, m] = selectedDate.split('-').map(Number);
      return new Date(y, m - 1);
    }
    return new Date();
  });
  const [hovered, setHovered] = useState(null);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];

    let startDay = first.getDay() - 1;
    if (startDay < 0) startDay = 6;
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const isDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      if (date < min) return true;
    }
    if (disabledDates.length > 0 && disabledDates.includes(formatDate(date))) return true;
    return false;
  };

  const prevMonth = () => {
    const now = new Date();
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    if (prev.getFullYear() > now.getFullYear() ||
        (prev.getFullYear() === now.getFullYear() && prev.getMonth() >= now.getMonth())) {
      setCurrentMonth(prev);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const days = getDays();
  const isToday = (date) => date && formatDate(date) === formatDate(new Date());
  const isSelected = (date) => date && selectedDate === formatDate(date);

  return (
    <div style={{
      background: '#fff', '--ch': '8px', '--stroke': '#D8DDDA', padding: 12,
      border: '1px solid #D8DDDA', boxShadow: '0 4px 16px rgba(28,61,46,0.10)',
      width: 280, userSelect: 'none'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <button onClick={prevMonth} style={navBtnStyle}>‹</button>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button onClick={nextMonth} style={navBtnStyle}>›</button>
      </div>

      {/* Weekday headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#5E6561', fontWeight: 500, padding: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
        ))}
      </div>

      {/* Days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {days.map((date, i) => {
          const disabled = isDisabled(date);
          const selected = isSelected(date);
          const today = isToday(date);
          const isHovered = hovered === i && !disabled && date;
          const dateStr = date ? formatDate(date) : null;
          const isGreen = dateStr && (coloredDates.green || []).includes(dateStr);
          const isRed = dateStr && (coloredDates.red || []).includes(dateStr);
          const isYellow = dateStr && (coloredDates.yellow || []).includes(dateStr);

          let bg = 'transparent';
          let color = !date ? 'transparent' : disabled ? '#A9B1AC' : selected ? '#fff' : today ? '#1C3D2E' : '#1C3D2E';
          if (selected) { bg = '#1C3D2E'; }
          else if (isHovered) { bg = '#DFF2E8'; }
          else if (isGreen) { bg = '#DFF2E8'; color = '#1C3D2E'; }
          else if (isYellow) { bg = '#FFE7DD'; color = '#1C3D2E'; }
          else if (isRed) { bg = '#FFE7DD'; color = '#B0603C'; }

          return (
            <div
              key={i}
              onClick={() => date && !disabled && onSelectDate(formatDate(date))}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, '--ch': '5px', '--stroke': (today && !selected ? '#1C3D2E' : 'transparent'),
                cursor: !date ? 'default' : disabled ? 'not-allowed' : 'pointer',
                color,
                background: bg,
                fontWeight: selected || today ? 700 : isRed ? 600 : 400,
                border: today && !selected ? '1px solid #1C3D2E' : '1px solid transparent',
                transition: 'background 0.15s'
              }}
            >
              {date ? date.getDate() : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const navBtnStyle = {
  width: 28, height: 28, border: '1px solid #D8DDDA', background: '#fff',
  '--ch': '5px', '--stroke': '#D8DDDA', cursor: 'pointer', fontSize: 16, display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: '#1C3D2E', fontWeight: 600
};

export default AdminCalendar;
